import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Nav
} from 'react-bootstrap';
import { RootState } from '../store';
import { UserPreferences } from '../api/generated/model';

const UserSettings: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [preferences, setPreferences] = useState<UserPreferences>(user?.preferences || {});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Unable to load user settings. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  const handleSaveSettings = async () => {
    try {
      // Here you would make an API call to save the preferences
      console.log('Saving preferences:', preferences);
      
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const updateDashboardSetting = (key: string, value: any) => {
    setPreferences(prev => ({
      ...prev,
      dashboard_settings: {
        ...prev.dashboard_settings,
        [key]: value
      }
    }));
  };

  const updateNotificationSetting = (key: string, value: any) => {
    setPreferences(prev => ({
      ...prev,
      notification_settings: {
        ...prev.notification_settings,
        [key]: value
      }
    }));
  };

  return (
    <Container fluid className="py-4">
      {showSuccessMessage && (
        <Alert variant="success" dismissible onClose={() => setShowSuccessMessage(false)}>
          <i className="bi bi-check-circle me-2"></i>
          Settings saved successfully!
        </Alert>
      )}

      <Card className="">
        <Card.Header className="">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">User Settings</h4>
            <Button variant="primary" onClick={handleSaveSettings}>
              <i className="bi bi-check me-1"></i>
              Save Settings
            </Button>
          </div>
        </Card.Header>
        
        <Card.Body>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item><Nav.Link active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')}><i className="bi bi-speedometer2 me-2"></i>Dashboard</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')}><i className="bi bi-bell me-2"></i>Notifications</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link active={activeTab === 'privacy'} onClick={() => setActiveTab('privacy')}><i className="bi bi-shield-check me-2"></i>Privacy & Security</Nav.Link></Nav.Item>
          </Nav>

          {activeTab === 'dashboard' && (
              <Row>
                <Col lg={6}>
                  <h5 className="mb-3">Dashboard Preferences</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Default Date Range</Form.Label>
                    <Form.Select 
                      value={preferences.dashboard_settings?.default_date_range || 'current_month'}
                      onChange={(e) => updateDashboardSetting('default_date_range', e.target.value)}
                    >
                      <option value="current_month">Current Month</option>
                      <option value="current_quarter">Current Quarter</option>
                      <option value="current_year">Current Year</option>
                      <option value="last_30_days">Last 30 Days</option>
                      <option value="last_90_days">Last 90 Days</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Choose the default date range when loading the dashboard
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Preferred Charts</Form.Label>
                    <div>
                      {['commission_trend', 'sales_performance', 'quota_progress', 'top_deals'].map(chart => (
                        <Form.Check
                          key={chart}
                          type="checkbox"
                          id={`chart-${chart}`}
                          label={chart.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          checked={preferences.dashboard_settings?.preferred_charts?.includes(chart) || false}
                          onChange={(e) => {
                            const currentCharts = preferences.dashboard_settings?.preferred_charts || [];
                            const newCharts = e.target.checked
                              ? [...currentCharts, chart]
                              : currentCharts.filter(c => c !== chart);
                            updateDashboardSetting('preferred_charts', newCharts);
                          }}
                        />
                      ))}
                    </div>
                    <Form.Text className="text-muted">
                      Select which charts you want to see by default on your dashboard
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col lg={6}>
                  <h5 className="mb-3">Table Settings</h5>
                  
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Column Order Preferences</strong><br />
                    You can drag and drop table columns to reorder them. Your preferences are automatically saved.
                  </Alert>

                  {preferences.table_column_orders && Object.keys(preferences.table_column_orders).length > 0 && (
                    <div>
                      <h6>Saved Column Orders:</h6>
                      <ul className="list-unstyled">
                        {Object.entries(preferences.table_column_orders).map(([tableName, columns]) => (
                          <li key={tableName} className="mb-2">
                            <strong>{tableName}:</strong>
                            <div className="text-muted small">
                              {Array.isArray(columns) ? columns.join(', ') : 'Custom order saved'}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
              </Row>
          )}

          {activeTab === 'notifications' && (
              <Row>
                <Col lg={6}>
                  <h5 className="mb-3">Notification Preferences</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="email-notifications"
                      label="Email Notifications"
                      checked={preferences.notification_settings?.email_enabled ?? true}
                      onChange={(e) => updateNotificationSetting('email_enabled', e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Receive notifications via email
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="push-notifications"
                      label="Browser Push Notifications"
                      checked={preferences.notification_settings?.push_enabled ?? false}
                      onChange={(e) => updateNotificationSetting('push_enabled', e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Receive push notifications in your browser
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Notification Frequency</Form.Label>
                    <Form.Select 
                      value={preferences.notification_settings?.frequency || 'immediate'}
                      onChange={(e) => updateNotificationSetting('frequency', e.target.value)}
                    >
                      <option value="immediate">Immediate</option>
                      <option value="daily">Daily Summary</option>
                      <option value="weekly">Weekly Summary</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      How often you want to receive notification summaries
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <h5 className="mb-3">Notification Types</h5>
                  
                  <Alert variant="light">
                    <h6>You'll receive notifications for:</h6>
                    <ul className="mb-0">
                      <li>New commission calculations</li>
                      <li>Commission payment updates</li>
                      <li>Quota achievements and milestones</li>
                      <li>Important deal updates</li>
                      <li>System announcements</li>
                    </ul>
                  </Alert>
                </Col>
              </Row>
          )}

          {activeTab === 'privacy' && (
              <Row>
                <Col lg={8}>
                  <h5 className="mb-3">Privacy & Security Settings</h5>
                  
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Data Privacy</strong><br />
                    Your personal information and commission data are encrypted and stored securely. 
                    Only authorized personnel can access your information based on your role permissions.
                  </Alert>

                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Account Security</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <strong>Password</strong>
                          <div className="text-muted small">Last changed: Recently</div>
                        </div>
                        <Button variant="outline-primary" size="sm" href="/profile">
                          <i className="bi bi-key me-1"></i>
                          Change Password
                        </Button>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Two-Factor Authentication</strong>
                          <div className="text-muted small">Add an extra layer of security</div>
                        </div>
                        <Button variant="outline-secondary" size="sm" disabled>
                          <i className="bi bi-shield-plus me-1"></i>
                          Enable 2FA
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Data & Privacy</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <strong>Download Your Data</strong>
                          <div className="text-muted small">Export your commission and sales data</div>
                        </div>
                        <Button variant="outline-info" size="sm">
                          <i className="bi bi-download me-1"></i>
                          Export Data
                        </Button>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Activity Log</strong>
                          <div className="text-muted small">View your account activity history</div>
                        </div>
                        <Button variant="outline-secondary" size="sm">
                          <i className="bi bi-clock-history me-1"></i>
                          View Activity
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UserSettings;